import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Avatar, 
  IconButton, 
  Stack, 
  Chip, 
  Divider, 
  Skeleton,
  useTheme,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Event as DateIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WhatsApp as WhatsAppIcon,
  Call as CallIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { Contact, ContactDto } from '../../../types/crm/types/contact';
import contactService from '../../../services/contactService';
import Swal from 'sweetalert2';
import { useAuth } from '../../../context/AuthContext';
import ContactEditPage from './ContactEditPage';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const ContactDetailPage: React.FC = () => {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [contact, setContact] = useState<ContactDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(true);
  const { userToken } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const [editContact, setEditContact] = useState<Contact | null>(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const activeProfile = userToken?.user.profiles.find(profile => profile.roleId === 3);

  useEffect(() => {
    const fetchContact = async () => {
      try {
        setLoading(true);
        const data = await contactService.getContactById(Number(contactId));
        setContact(data);

        const editData: Contact = {
          fullName: data?.fullName ?? '',
          phone: data?.phone ?? '',
          type: data?.contactType ?? '', 
          email: data?.email ?? '',
          address: data?.address ?? '',
          userProfileId: activeProfile?.id ?? 0
        };
        setEditContact(editData);

        setError(null);
      } catch (err) {
        setError('Error al cargar los detalles del contacto');
        console.error('Error fetching contact:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchContact();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const handleClose = () => {
    setOpen(false);
    navigate(-1);
  };

  const handleEditContact = () => {
    setEditOpen(true);
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
  };

  const handleEditSuccess = () => {
  setShowSuccessToast(true);
  setTimeout(() => setShowSuccessToast(false), 3000); 
};


  const handleDeleteContact = async () => {
  const userProfileId = activeProfile?.id;
  if (!contact || !userProfileId) return;

  const confirmResult = await Swal.fire({
    title: "¿Estás seguro?",
    text: "Esta acción eliminará el contacto de forma permanente.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    customClass: {
      container: "custom-popup",
    },
  });

  if (!confirmResult.isConfirmed) return;

  try {
    const result = await contactService.deleteContact(contact.id, userProfileId);

    if (result.success) {
      Swal.fire({
        title: "Eliminado",
        text: result.message,
        icon: "success",
        confirmButtonText: "Aceptar",
        customClass: {
          container: "custom-popup",
        },
      }).then(() => {
        navigate(-1);
      });
    } else {
      await Swal.fire({
        title: "Error",
        text: result.message,
        icon: "error",
        confirmButtonText: "Aceptar",
        customClass: {
          container: "custom-popup",
        },
      });
    }
  } catch (err) {
    console.error(err);
    await Swal.fire({
      title: "Error",
      text: "Ocurrió un error al eliminar el contacto.",
      icon: "error",
      confirmButtonText: "Aceptar",
      customClass: {
        container: "custom-popup",
      },
    });
  }
};

  const handleCall = () => {
    if (contact?.phone) {
      window.open(`tel:${contact.phone}`, '_self');
    }
  };

  const handleWhatsApp = () => {
    if (contact?.phone) {
      // Número en formato internacional sin +, con código país. Ajusta si es necesario.
      const phoneNumber = contact.phone.replace(/\D/g, '');
      window.open(`https://wa.me/${phoneNumber}`, '_blank');
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" PaperProps={{ sx: { bgcolor: 'rgba(255,255,255,0.9)' } }}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">Cargando contacto...</Typography>
            <IconButton onClick={handleClose}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Skeleton variant="circular" width={150} height={150} />
          </Box>
          <Box sx={{ px: 4 }}>
            <Skeleton variant="text" width="60%" height={40} />
            <Skeleton variant="text" width="40%" height={30} />
            <Skeleton variant="rectangular" width="100%" height={200} sx={{ mt: 3 }} />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error || !contact) {
    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: { bgcolor: 'rgba(255,255,255,0.9)' } }}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" color="error">Error</Typography>
            <IconButton onClick={handleClose}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography>{error || 'Contacto no encontrado'}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth="lg"
      PaperProps={{
        sx: {
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 3,
          backdropFilter: 'blur(8px)', 
          boxShadow: theme.shadows[5],
          p: 3
        }
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Detalles del Contacto
          </Typography>
          <IconButton onClick={handleClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {showSuccessToast && (
        <Box sx={{
          position: 'fixed',
          top: 20,
          right: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: 'background.paper',
          color: 'success.main',
          border: theme => `1px solid ${theme.palette.success.main}`,
          p: 1.5,
          px: 2,
          borderRadius: 3,
          boxShadow: 4,
          zIndex: theme => theme.zIndex.snackbar,
          minWidth: 250,
          fontSize: '0.9rem',
          fontWeight: 500,
          transition: 'all 0.3s ease-in-out',
        }}>
          <CheckCircleIcon sx={{ color: 'success.main' }} />
          <span>¡Contacto actualizado con éxito!</span>
        </Box>
      )}

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 6 }}>
          {/* Izquierda: Avatar e info básica */}
          <Box sx={{ minWidth: 280, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Avatar
              src={contact.userProfileImage || '/avatar-placeholder.png'}
              sx={{ width: 160, height: 160, mb: 3, border: `4px solid ${theme.palette.primary.main}` }}
            />
            <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
              {contact.fullName || 'Nombre no disponible'}
            </Typography>
            <Chip
              label={contact.contactType || 'Sin tipo'}
              color={
                contact.contactType === 'Cliente' ? 'primary' :
                contact.contactType === 'Proveedor' ? 'secondary' : 'default'
              }
              sx={{ mt: 2, mb: 2, borderRadius: 1, fontWeight: 'bold' }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEditContact}
                sx={{ borderRadius: 2 }}
              >
                Editar
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteContact}
                sx={{ borderRadius: 2 }}
              >
                Eliminar
              </Button>
            </Stack>
          </Box>

          {/* Derecha: Detalles y acciones */}
          <Box sx={{ flex: 1 }}>
            <Stack spacing={3}>
              {/* Contact info */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                  Información de Contacto
                </Typography>
                <Stack spacing={2}>
                  {contact.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PhoneIcon color="primary" sx={{ mr: 2 }} />
                      <Typography>{contact.phone}</Typography>
                    </Box>
                  )}
                  {contact.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <EmailIcon color="primary" sx={{ mr: 2 }} />
                      <Typography>{contact.email}</Typography>
                    </Box>
                  )}
                  {contact.address && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationIcon color="primary" sx={{ mr: 2 }} />
                      <Typography>{contact.address}</Typography>
                    </Box>
                  )}
                </Stack>
              </Box>

              <Divider />

              {/* Additional info */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                  Información Adicional
                </Typography>
                <Stack spacing={2}>
                  {contact.createdAt && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DateIcon color="primary" sx={{ mr: 2 }} />
                      <Typography>Fecha de creación: {new Date(contact.createdAt).toLocaleDateString()}</Typography>
                    </Box>
                  )}
                  {contact.updatedAt && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DateIcon color="primary" sx={{ mr: 2 }} />
                      <Typography>Última actualización: {new Date(contact.updatedAt).toLocaleDateString()}</Typography>
                    </Box>
                  )}
                </Stack>
              </Box>

              <Divider />

              {/* Acción rápida: llamar o whatsapp */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                  Acciones rápidas
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<WhatsAppIcon />}
                    onClick={handleWhatsApp}
                    disabled={!contact.phone}
                    sx={{ flex: 1 }}
                  >
                    Enviar WhatsApp
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CallIcon />}
                    onClick={handleCall}
                    disabled={!contact.phone}
                    sx={{ flex: 1 }}
                  >
                    Llamar
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Box>
          {editOpen && (
          <ContactEditPage
            id={Number(contactId)}
            contact={editContact!}
            onClose={handleCloseEdit}
            onUpdate={handleEditSuccess}
          />
        )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ContactDetailPage;
